import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Calendar,
  DollarSign,
  Building,
  Users,
  CreditCard,
  Edit,
  Flag,
  CheckSquare,
  Square,
  X
} from 'lucide-react';
import api from '../../utils/api';
import { getDisplayAmount } from '../../utils/amountUtils';
import InvoicePreview from '../shared/InvoicePreview';
import { downloadInvoice } from '../../utils/invoiceDownload';

const InvoiceOversight = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    start_date: '',
    end_date: '',
    search: ''
  });

  // Modal states
  const [showInvoiceDetailModal, setShowInvoiceDetailModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Bulk actions
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const handlePreviewDownload = async (format, invoice) => {
    if (!invoice) {
      alert('No invoice selected');
      return;
    }

    try {
      await downloadInvoice(
        invoice,
        format === 'excel' ? 'csv' : format,
        {
          setLoading,
          ...(format === 'pdf' ? { forcePrintablePreview: true } : {})
        }
      );
    } catch (downloadError) {
      console.error('Error downloading invoice:', downloadError);
      alert('Failed to download invoice. Please try again.');
    }
  };

  // Fetch invoices on component mount and filter changes
  useEffect(() => {
    fetchInvoices();
  }, [filters, pagination.page]);

  // Fetch invoices with filters
  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await api.get(`/api/dashboard/staff/invoices?${params}`);
      
      if (response?.data?.success && response?.data?.data) {
        // API returns: { success: true, data: { invoices: [], total: X, page: Y, limit: Z, totalPages: W } }
        const invoicesData = response.data.data.invoices || [];
        const total = response.data.data.total || 0;
        const page = response.data.data.page || pagination.page;
        const limit = response.data.data.limit || pagination.limit;
        const totalPages = response.data.data.totalPages || Math.ceil(total / limit);
        
        setInvoices(invoicesData);
        setPagination({
          page,
          limit,
          total,
          pages: totalPages
        });
      } else {
        setError(response?.data?.error || 'Failed to fetch invoices');
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        setError(`Failed to fetch invoices: ${error.response.data.error || 'Unknown error'}`);
      } else {
        setError('Failed to fetch invoices');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch clients for dropdown - not needed for invoice oversight
  const fetchClients = async () => {
    // Client filter removed - not essential for invoice oversight
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchInvoices();
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      start_date: '',
      end_date: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    try {
      setLoading(true);
      const response = await api.post('/api/admin/invoices/bulk-review', {
        invoice_ids: selectedInvoices,
        action,
        notes: `Bulk ${action} action`
      });
      
      if (response.data.success) {
        setSelectedInvoices([]);
        fetchInvoices(); // Refresh the list
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
      setError('Failed to perform bulk action');
    } finally {
      setLoading(false);
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      const params = new URLSearchParams(filters);
      const response = await api.get(`/api/admin/invoices/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoices-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting invoices:', error);
      setError('Failed to export invoices');
    }
  };

  // Handle invoice selection - fetch complete invoice details from API
  const handleInvoiceSelect = async (invoice) => {
    try {
      console.log('📋 Fetching complete invoice details for:', invoice.id);
      const response = await api.get(`/api/staff/invoices/${invoice.id}`);
      console.log('📋 API response:', response.data);
      
      if (response.data && response.data.data) {
        setSelectedInvoice(response.data.data);
        setShowInvoiceDetailModal(true);
      } else {
        console.error('Invalid API response structure');
        // Fallback to basic data from list
        setSelectedInvoice(invoice);
        setShowInvoiceDetailModal(true);
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      // Fallback to basic data from list
      setSelectedInvoice(invoice);
      setShowInvoiceDetailModal(true);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const config = {
      paid: { color: 'bg-green-100 text-green-800' },
      unpaid: { color: 'bg-red-100 text-red-800' },
      overdue: { color: 'bg-orange-100 text-orange-800' },
      refunded: { color: 'bg-blue-100 text-blue-800' },
      cancelled: { color: 'bg-gray-100 text-gray-800' }
    };
    return config[status] || { color: 'bg-gray-100 text-gray-800' };
  };

  // Get source color
  const getSourceColor = (source) => {
    const config = {
      system: { color: 'bg-blue-100 text-blue-800' },
      manual: { color: 'bg-purple-100 text-purple-800' },
      'client-uploaded': { color: 'bg-green-100 text-green-800' }
    };
    return config[source] || { color: 'bg-gray-100 text-gray-800' };
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoice Oversight</h1>
          <p className="text-gray-600">Monitor and manage all client invoices</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          <button
            onClick={clearFilters}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear all
          </button>
        </div>

        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">


          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="overdue">Overdue</option>
              <option value="refunded">Refunded</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="flex">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by invoice ID, client name, or email..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <Search className="w-4 h-4" />
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Bulk Actions */}
      {selectedInvoices.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckSquare className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                {selectedInvoices.length} invoice(s) selected
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handleBulkAction('approve')}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Approve All
              </button>
              <button
                onClick={() => handleBulkAction('flag')}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
              >
                <Flag className="w-3 h-3 mr-1" />
                Flag All
              </button>
              <button
                onClick={() => handleBulkAction('mark_paid')}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
              >
                <DollarSign className="w-3 h-3 mr-1" />
                Mark as Paid
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Invoices</h3>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading invoices...</p>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchInvoices}
              className="mt-2 text-blue-600 hover:text-blue-800"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedInvoices(invoices.map(inv => inv.id));
                          } else {
                            setSelectedInvoices([]);
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dates
                    </th>

                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedInvoices.includes(invoice.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedInvoices(prev => [...prev, invoice.id]);
                            } else {
                              setSelectedInvoices(prev => prev.filter(id => id !== invoice.id));
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {invoice.invoiceNumber || `INV-${invoice.id}`}
                        </div>
                        <div className="text-sm text-gray-500">
                          #{invoice.id.substring(0, 8)}...
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {invoice.receiver?.companyName || invoice.receiver?.company || invoice.receiver?.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {invoice.receiver?.email || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          £{getDisplayAmount(invoice.total).toFixed(2)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {invoice.currency || 'GBP'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status).color}`}>
                          {invoice.status || 'unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          Created: {invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString('en-GB') : 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          Due: {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-GB') : 'N/A'}
                        </div>
                        {invoice.paymentDate && (
                          <div className="text-sm text-green-600">
                            Paid: {new Date(invoice.paymentDate).toLocaleDateString('en-GB')}
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleInvoiceSelect(invoice)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm text-gray-700">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.pages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Invoice Preview Modal - Using shared professional component */}
      {showInvoiceDetailModal && selectedInvoice && (
        <InvoicePreview
          invoice={selectedInvoice}
          isOpen={showInvoiceDetailModal}
          onClose={() => {
            setShowInvoiceDetailModal(false);
            setSelectedInvoice(null);
          }}
          onDownloadPDF={(invoice) => handlePreviewDownload('pdf', invoice)}
          onDownloadExcel={(invoice) => handlePreviewDownload('excel', invoice)}
          showDownloadButtons={true}
          title="Invoice Details"
        />
      )}
    </div>
  );
};

export default InvoiceOversight;
